import React, { useState } from 'react';
import { Box, FormControl, IconButton, MenuItem, Popover, Select, Tooltip } from '@mui/material';
import { SketchPicker, ColorResult } from 'react-color';
import SliderWithInput from '../../SliderWithInput';
import SwitchWithInput from '../../SwitchWithInput';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface GridControlsProps {
  axisType: 'x' | 'y';
  propKey: string;
  property: any;
  axisProps: any;
  enableGrids: (propKey: string, option: string, value: any) => void;
  updateAxisOptions: (axis: string, option: string, value: any, series?: string) => void;
  isMultiSeries?: boolean;
  iconColor?: string;
}

const GridControls: React.FC<GridControlsProps> = ({
  axisType,
  propKey,
  property,
  axisProps,
  enableGrids,
  updateAxisOptions,
  isMultiSeries = false,
  iconColor,
}) => {
  const { chartType, xAxisOptions, yAxisOptions } = useSelector((state: RootState) => ({
    chartType: state.chartProperties.properties[propKey].chartType,
    xAxisOptions: state.chartControls.properties[propKey]?.axisOptions?.xAxis,
    yAxisOptions: state.chartControls.properties[propKey]?.axisOptions?.yAxis,
  }));

  const [isColorPopoverOpen, setColorPopoverOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeSeriesForColor, setActiveSeriesForColor] = useState<string | null>(null);

  const [isSeriesExpanded, setIsSeriesExpanded] = useState<{ [key: string]: boolean }>({
    '1': true,
    '2': false,
    '3': false,
    '4': false,
  });

  const handleColorClick = (event: React.MouseEvent<HTMLElement>, series?: string) => {
    setAnchorEl(event.currentTarget);
    setColorPopoverOpen(true);
    setActiveSeriesForColor(series || null);
  };

  const handleColorClose = () => {
    setAnchorEl(null);
    setColorPopoverOpen(false);
    setActiveSeriesForColor(null);
  };

  const gridLabel = () => {
    if (['heatmap'].includes(chartType)) {
      return axisType === 'x' ? 'ROW GRID' : 'COLUMN GRID';
    }

    if (['horizontalBar', 'horizontalStacked'].includes(chartType)) {
      return axisType === 'x' ? 'MEASURE GRID' : 'DIMENSION GRID';
    }

    if (['scatterPlot'].includes(chartType)) {
      return axisType === 'x' ? 'X-GRID' : 'Y-GRID';
    }

    return axisType === 'x' ? 'DIMENSION GRID' : 'MEASURE GRID';
  };

  const axisKey = `${axisType}Axis`;

  // Get axis options for multi-series
  const axisOptions = axisType === 'x' ? xAxisOptions : yAxisOptions;
  const seriesKeys = isMultiSeries ? Object.keys(axisOptions || {}) : [];

  // Render grid options for a single series
  const renderGridOptions = (seriesAxisProps: any, seriesKey?: string) => {
    const isMultiSeriesItem = seriesKey ? true : false;

    return (
      <div style={{ marginLeft: '10px', marginBottom: isMultiSeriesItem ? '15px' : '20px' }}>
        <div
          className="optionDescription"
          style={{
            marginTop: '-10px',
            transform: 'translateX(-10px)',
            marginBottom: '5px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          Color
        </div>
        <div
          style={{
            width: '25%',
            height: '1.25rem',
            backgroundColor: seriesAxisProps?.splitLineColor || '#666666',
            border: '2px solid darkgray',
            cursor: 'pointer',
            marginTop: '-4px',
            marginBottom: '-2px',
            transform: 'TranslateX(-3px)',
          }}
          onClick={(e) => handleColorClick(e, seriesKey)}
        />

        <Popover
          open={
            isColorPopoverOpen && (isMultiSeriesItem ? activeSeriesForColor === seriesKey : true)
          }
          anchorEl={anchorEl}
          onClose={handleColorClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              boxShadow: 'none',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: 'white',
            },
          }}
        >
          <div style={{ padding: '0' }}>
            <SketchPicker
              color={seriesAxisProps?.splitLineColor || '#d3d3d3'}
              onChange={(color: ColorResult) =>
                updateAxisOptions(axisKey, 'splitLineColor', color.hex, seriesKey)
              }
              disableAlpha
              width="13rem"
              styles={{
                default: {
                  picker: {
                    boxShadow: 'none',
                    borderRadius: '6px',
                    width: '13rem',
                  },
                },
              }}
            />
          </div>
        </Popover>

        <div
          className="optionDescription"
          style={{
            marginLeft: '-10px',
            marginBottom: '5px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          Thickness
        </div>
        <div style={{ marginLeft: '-7px' }}>
          <SliderWithInput
            sliderValue={seriesAxisProps?.splitLineWidth ?? 1}
            sliderMinMax={{ min: 0, max: 10, step: 0.5 }}
            changeValue={(value) => updateAxisOptions(axisKey, 'splitLineWidth', value, seriesKey)}
          />
        </div>

        <div
          className="optionDescription"
          style={{
            marginTop: '10px',
            marginLeft: '-10px',
            marginBottom: '5px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          Line Type
        </div>
        <FormControl fullWidth size="small" style={{ marginLeft: '-5px' }}>
          <Select
            value={seriesAxisProps?.splitLineType || 'solid'}
            onChange={(e) => updateAxisOptions(axisKey, 'splitLineType', e.target.value, seriesKey)}
            style={{
              height: '25px',
              color: iconColor ? iconColor : '#404040',
            }}
            sx={{
              '&.MuiOutlinedInput-root': {
                '& fieldset': {
                  border: '1px solid rgb(211, 211, 211)', // Change the border color here
                },
                '&:hover fieldset': {
                  border: '1px solid #2bb9bb', // Change the hover border color here
                },
                '&.Mui-focused fieldset': {
                  border: '1px solid #2bb9bb', // Change the focused border color here
                },
                '&.Mui-focused svg': {
                  color: '#2bb9bb', // Change the arrow color when focused
                },
                '& svg': {
                  color: iconColor ? iconColor : '#404040',
                },
              },
            }}
          >
            <MenuItem value="solid">Solid</MenuItem>
            <MenuItem value="dashed">Dashed</MenuItem>
            <MenuItem value="dotted">Dotted</MenuItem>
          </Select>
        </FormControl>
      </div>
    );
  };

  // Render multi-series controls
  const renderMultiSeriesControls = () => {
    return (
      <div style={{ marginTop: '5px' }}>
        {seriesKeys.map((seriesKey: string) => {
          const seriesAxisProps = axisOptions[seriesKey];

          return (
            <div key={seriesKey} style={{ marginBottom: '10px' }}>
              <Box
                style={{
                  display: 'flex',
                  marginBottom: '5px',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: '12px',
                    color: iconColor ? iconColor : '#515050',
                    marginLeft: '0.5rem',
                  }}
                >
                  Series {seriesKey}
                </span>
                <Tooltip
                  title={isSeriesExpanded[seriesKey] ? 'Click to Collapse' : 'Click to Expand'}
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    disableRipple
                    disableFocusRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSeriesExpanded((prev) => ({
                        ...prev,
                        [seriesKey]: !prev[seriesKey],
                      }));
                    }}
                    aria-label={isSeriesExpanded[seriesKey] ? 'Collapse Series' : 'Expand Series'}
                    sx={{
                      cursor: 'default',
                      paddingTop: '5px',
                      transform: 'TranslateY(-5px)',
                      '&:hover': {
                        backgroundColor: 'white',
                      },
                      '&:focus, &:active': {
                        backgroundColor: 'white',
                        outline: 'none',
                      },
                    }}
                  >
                    {isSeriesExpanded[seriesKey] ? (
                      <KeyboardArrowDownIcon fontSize="medium" />
                    ) : (
                      <ChevronRightIcon fontSize="medium" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {isSeriesExpanded[seriesKey] && renderGridOptions(seriesAxisProps, seriesKey)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ marginTop: '10px' }}>
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingTop: '0px',
        }}
      >
        <span style={{ color: iconColor ? iconColor : '#404040' }}>{gridLabel()}</span>
        <FormControl
          size="small"
          sx={{
            transform: 'translateX(65px)',
            '@supports (-moz-appearance: none)': {
              transform: 'translateX(58px)',
            },
          }}
        >
          <SwitchWithInput
            isChecked={property[`${axisType}SplitLine`] ?? true}
            onSwitch={() =>
              enableGrids(
                propKey,
                `${axisType}SplitLine`,
                !(property[`${axisType}SplitLine`] ?? true),
              )
            }
          />
        </FormControl>
      </div>

      {property[`${axisType}SplitLine`] &&
        (isMultiSeries ? renderMultiSeriesControls() : renderGridOptions(axisProps))}
    </div>
  );
};

export default GridControls;
